'use client';
import React, { useMemo } from 'react';
import type { ColumnDef } from '@benzinga/table';
import type { Ratings } from '@benzinga/calendar-manager';
import { formatDate, isoToMillis } from './formatters';
import QuoteListTable from './QuoteListTable';

type ActionBadge = { label: string; className: string };

const getActionBadge = (rating: Ratings): ActionBadge => {
  switch (rating.action_company) {
    case 'Upgrades':
      return { label: 'Upgrade', className: 'bg-green-100 text-green-700' };
    case 'Downgrades':
      return { label: 'Downgrade', className: 'bg-red-100 text-red-700' };
    case 'Initiates Coverage On':
      return { label: 'Initiate', className: 'bg-blue-100 text-blue-700' };
    case 'Assumes':
      return { label: 'Assumes', className: 'bg-purple-100 text-purple-700' };
    case 'Maintains':
    case 'Reiterates':
      if (rating.action_pt === 'Raises') return { label: 'PT Raise', className: 'bg-teal-100 text-teal-700' };
      if (rating.action_pt === 'Lowers') return { label: 'PT Cut', className: 'bg-orange-100 text-orange-700' };
      return { label: 'Maintained', className: 'bg-gray-100 text-gray-500' };
    default:
      if (rating.action_pt === 'Raises') return { label: 'PT Raise', className: 'bg-teal-100 text-teal-700' };
      if (rating.action_pt === 'Lowers') return { label: 'PT Cut', className: 'bg-orange-100 text-orange-700' };
      return { label: rating.action_company ?? 'Maintained', className: 'bg-gray-100 text-gray-500' };
  }
};

const PriceTargetCell: React.FC<{ rating: Ratings }> = ({ rating }) => {
  const current = rating.pt_current ? Number(rating.pt_current) : null;
  const prior = rating.pt_prior ? Number(rating.pt_prior) : null;
  if (!current) return <span className="text-gray-400">—</span>;
  if (!prior || prior === current) return <span>${current.toFixed(0)}</span>;
  const isRaise = current > prior;
  return (
    <span>
      <span className="text-gray-400 mr-0.5">${prior.toFixed(0)}</span>
      <span className="text-gray-400 mx-0.5">→</span>
      <span className={isRaise ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
        ${current.toFixed(0)}
      </span>
    </span>
  );
};

const analystActionsColumnsDef: ColumnDef<Ratings>[] = [
  {
    cellClass: 'text-gray-600',
    field: 'date',
    headerName: 'Date',
    valueFormatter: ({ data }) => formatDate(data.date),
  },
  {
    cellClass: 'font-semibold text-gray-800',
    field: 'analyst',
    headerName: 'Firm',
    valueFormatter: ({ data }) => data.analyst || '—',
  },
  {
    cellClass: 'text-gray-600',
    field: 'analyst_name',
    headerName: 'Analyst',
    valueFormatter: ({ data }) => data.analyst_name || '—',
  },
  {
    cellRenderer: ({ data }) => {
      const badge = getActionBadge(data);
      return (
        <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${badge.className}`}>
          {badge.label}
        </span>
      );
    },
    field: 'action_company',
    headerName: 'Action',
  },
  {
    cellRenderer: ({ data }) =>
      data.rating_prior && data.rating_prior !== data.rating_current ? (
        <span>
          <span className="text-gray-400">{data.rating_prior}</span>
          <span className="mx-1 text-gray-400">→</span>
          <span className="font-semibold">{data.rating_current}</span>
        </span>
      ) : (
        data.rating_current || '—'
      ),
    field: 'rating_current',
    headerName: 'Rating Change',
  },
  {
    cellClass: 'text-right text-gray-700',
    cellRenderer: ({ data }) => <PriceTargetCell rating={data} />,
    field: 'pt_current',
    headerClass: 'text-right',
    headerName: 'Price Target',
  },
];

export const AnalystActionsTable: React.FC<{ ratings: Ratings[] }> = ({ ratings }) => {
  const recent = useMemo(
    () => [...ratings].sort((a, b) => isoToMillis(b.date) - isoToMillis(a.date)).slice(0, 20),
    [ratings],
  );

  if (!recent.length) return null;

  return (
    <QuoteListTable columnsDef={analystActionsColumnsDef} gateType="analyst-ratings" rowData={recent} />
  );
};

export default AnalystActionsTable;
